/**
 * ChannelPlannerView — compare outreach channels by cost per expected vote.
 *
 * The operator defines a compact audience (contest scope via candidates OR the
 * whole tenant, a cumulative tier, election), hits Analyze, and the view POSTs
 * both `/outreach/cost-per-vote` and `/outreach/decision-matrix`, then renders:
 *
 *   a) a cost-per-vote table (channel · cost · expected reach · cost/vote) with
 *      a small bar per row so the cheapest channel visually pops; and
 *   b) the live weighted decision matrix (rows = channels ranked by weighted
 *      total, columns = the six criteria scored 1–5, an emphasized weighted
 *      total, the winning row highlighted, and the `cost_efficiency` column
 *      flagged as live/derived).
 *
 * The operator may also tweak the six criteria weights and re-post the matrix
 * (the cost-per-vote table is weight-independent, so it is not refetched).
 *
 * Roles: {ADMIN, ORG_MANAGER, MANAGER, CAMPAIGN_MANAGER, ANALYST} — same gate
 * as the Mail List preview; the server enforces the same. Everyone else gets an
 * in-component "Access restricted" wall.
 *
 * Pattern mirrors MailListBuilderView: axios + useState/useEffect (not React
 * Query), design-system tokens, ViewHeader with an `actions` slot.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Scale, Loader2, RotateCcw, Wallet } from 'lucide-react';
import {
  getChannels,
  getContactPlan,
  getCostPerVote,
  getDecisionMatrix,
  listCandidates,
  type ChannelPlanSpec,
  type ChannelsCatalog,
  type ContactPlanResponse,
  type ContactPlanSpec,
  type CostPerVoteResponse,
  type DecisionMatrixResponse,
  type MailListTier,
  type PlanRow,
} from '@/api/channels';
import type { CandidateListItem } from '@/types/candidate';
import { candidateShortName } from '@/types/candidate';
import { ViewHeader } from '@/components/ViewHeader';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { roleMatches } from '@/lib/roles';

// ---------------------------------------------------------------------------
// Role gate
// ---------------------------------------------------------------------------

const PLANNER_ROLES = new Set(['ADMIN', 'ORG_MANAGER', 'MANAGER', 'SMS_MANAGER', 'ANALYST']);

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

const DEFAULT_ELECTION_DATE = '2026-08-04';
const DEFAULT_ELECTION_TYPE = 'primary';
const DEFAULT_TIER: MailListTier = 't1_2';

const TIER_OPTIONS: Array<{ value: MailListTier; label: string }> = [
  { value: 't1', label: 'T1 only' },
  { value: 't1_2', label: 'T1 + T2' },
  { value: 't1_2_3', label: 'T1 + T2 + T3' },
];

// ---- Budget optimizer ----------------------------------------------------

/**
 * The paid channels the budget optimizer may fund. Mail/SMS/digital boost are
 * on by default; canvass is opt-in (it competes on cost but is labor-bound).
 */
const PLAN_CHANNEL_OPTIONS: Array<{ value: string; label: string; defaultOn: boolean }> = [
  { value: 'mail', label: 'Mail', defaultOn: true },
  { value: 'sms', label: 'SMS', defaultOn: true },
  { value: 'digital_boost', label: 'Digital boost', defaultOn: true },
  { value: 'canvass', label: 'Canvass', defaultOn: false },
];

const DEFAULT_PLAN_CHANNELS: string[] = PLAN_CHANNEL_OPTIONS.filter((o) => o.defaultOn).map(
  (o) => o.value,
);

const DEFAULT_BUDGET_DOLLARS = '5000';
const DEFAULT_MAX_TIER: MailListTier = 't1_2';

/** The six decision criteria, in a stable display order, with human labels. */
const CRITERIA_ORDER = [
  'audience_fit',
  'cost_efficiency',
  'timeline',
  'persuasion',
  'targeting',
  'effort',
] as const;

const CRITERIA_LABELS: Record<string, string> = {
  audience_fit: 'Audience fit',
  cost_efficiency: 'Cost efficiency',
  timeline: 'Timeline',
  persuasion: 'Persuasion',
  targeting: 'Targeting',
  effort: 'Effort',
};

/** The one criterion whose score is computed live from the cost engine. */
const DERIVED_CRITERION = 'cost_efficiency';

function criterionLabel(key: string): string {
  return CRITERIA_LABELS[key] ?? key.replace(/_/g, ' ');
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

/** Cents → "$1,234" (whole dollars, rounded). */
function formatCents(cents: number): string {
  const dollars = Math.round((cents ?? 0) / 100);
  return `$${dollars.toLocaleString('en-US')}`;
}

/**
 * Cost-per-vote cents → "$0.06". These are small fractions of a dollar, so
 * unlike whole-dollar costs we keep two decimals. `null` (zero reach) → "—".
 */
function formatCostPerVote(cents: number | null): string {
  if (cents == null) return '—';
  return `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatCount(n: number): string {
  return (n ?? 0).toLocaleString('en-US');
}

/** A 0–5 score → "4.2" (drops a trailing ".0"). */
function formatScore(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface ChannelPlannerViewProps {
  userRole?: string;
}

export function ChannelPlannerView({ userRole }: ChannelPlannerViewProps) {
  const roleNorm = typeof userRole === 'string' ? userRole.toUpperCase() : '';
  const canView = roleMatches(roleNorm, PLANNER_ROLES);
  const isMobile = useIsMobile();

  // ---- Audience form state ------------------------------------------------
  const [electionDate, setElectionDate] = useState(DEFAULT_ELECTION_DATE);
  const [tier, setTier] = useState<MailListTier>(DEFAULT_TIER);

  const [candidates, setCandidates] = useState<CandidateListItem[]>([]);
  const [candidatesError, setCandidatesError] = useState<string | null>(null);
  const [selectedCandidateIds, setSelectedCandidateIds] = useState<number[]>([]);

  // ---- Weight state (the six criteria) ------------------------------------
  // Seeded from GET /outreach/channels, then editable. `null` until loaded.
  const [defaultWeights, setDefaultWeights] = useState<Record<string, number> | null>(null);
  const [weights, setWeights] = useState<Record<string, number>>({});

  // ---- Request state ------------------------------------------------------
  const [costResult, setCostResult] = useState<CostPerVoteResponse | null>(null);
  const [matrixResult, setMatrixResult] = useState<DecisionMatrixResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [reweighting, setReweighting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Whether the user has run at least one analysis (so weight edits can refetch).
  const [analyzed, setAnalyzed] = useState(false);

  // ---- Budget optimizer state ---------------------------------------------
  const [budgetDollars, setBudgetDollars] = useState(DEFAULT_BUDGET_DOLLARS);
  const [planChannels, setPlanChannels] = useState<string[]>(DEFAULT_PLAN_CHANNELS);
  const [maxTier, setMaxTier] = useState<MailListTier>(DEFAULT_MAX_TIER);
  const [planResult, setPlanResult] = useState<ContactPlanResponse | null>(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [planError, setPlanError] = useState<string | null>(null);

  // Load candidates for the multi-select (best-effort — whole-tenant works without).
  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    listCandidates(true)
      .then((rows) => {
        if (cancelled) return;
        setCandidates(rows);
      })
      .catch(() => {
        if (cancelled) return;
        setCandidatesError('Could not load candidates — the whole tenant will be used.');
      });
    return () => {
      cancelled = true;
    };
  }, [canView]);

  // Load the channel catalog to seed the criteria weights.
  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    getChannels()
      .then((cat: ChannelsCatalog) => {
        if (cancelled) return;
        setDefaultWeights(cat.criteria_weights);
        setWeights(cat.criteria_weights);
      })
      .catch(() => {
        /* Non-fatal — Analyze falls back to server defaults when weights is {}. */
      });
    return () => {
      cancelled = true;
    };
  }, [canView]);

  const buildSpec = useCallback((): ChannelPlanSpec => {
    const spec: ChannelPlanSpec = {
      election_date: electionDate,
      election_type: DEFAULT_ELECTION_TYPE,
      tier,
    };
    if (selectedCandidateIds.length > 0) {
      spec.candidate_ids = selectedCandidateIds;
    }
    return spec;
  }, [electionDate, tier, selectedCandidateIds]);

  /** Only send `weights` when the operator has diverged from the defaults. */
  const weightOverride = useMemo((): Record<string, number> | undefined => {
    if (!defaultWeights) return undefined;
    const changed = CRITERIA_ORDER.some(
      (k) => (weights[k] ?? 0) !== (defaultWeights[k] ?? 0),
    );
    return changed ? weights : undefined;
  }, [weights, defaultWeights]);

  const parseError = useCallback((e: unknown): string => {
    const status =
      typeof e === 'object' && e !== null && 'response' in e
        ? (e as { response?: { status?: number } }).response?.status
        : undefined;
    return status === 403
      ? 'Your role does not have access to the channel planner.'
      : status === 422
        ? 'The audience definition was rejected (422). Check the contest and election fields.'
        : status
          ? `Backend error ${status}.`
          : 'Could not reach the backend.';
  }, []);

  const handleAnalyze = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const spec = buildSpec();
      const [cost, matrix] = await Promise.all([
        getCostPerVote(spec),
        getDecisionMatrix(spec, weightOverride ? { weights: weightOverride } : undefined),
      ]);
      setCostResult(cost);
      setMatrixResult(matrix);
      setAnalyzed(true);
    } catch (e: unknown) {
      setError(parseError(e));
      setCostResult(null);
      setMatrixResult(null);
    } finally {
      setLoading(false);
    }
  }, [buildSpec, weightOverride, parseError]);

  /** Re-post ONLY the decision matrix after a weight tweak (cost is weight-independent). */
  const handleReweight = useCallback(async () => {
    if (!analyzed) return;
    setReweighting(true);
    setError(null);
    try {
      const matrix = await getDecisionMatrix(
        buildSpec(),
        weightOverride ? { weights: weightOverride } : undefined,
      );
      setMatrixResult(matrix);
    } catch (e: unknown) {
      setError(parseError(e));
    } finally {
      setReweighting(false);
    }
  }, [analyzed, buildSpec, weightOverride, parseError]);

  const resetWeights = useCallback(() => {
    if (defaultWeights) setWeights(defaultWeights);
  }, [defaultWeights]);

  const toggleCandidate = useCallback((id: number) => {
    setSelectedCandidateIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }, []);

  const setWeight = useCallback((key: string, raw: string) => {
    const parsed = Number.parseFloat(raw);
    setWeights((prev) => ({ ...prev, [key]: Number.isFinite(parsed) && parsed >= 0 ? parsed : 0 }));
  }, []);

  const weightsDirty = weightOverride !== undefined;

  // ---- Budget optimizer ---------------------------------------------------

  const togglePlanChannel = useCallback((value: string) => {
    setPlanChannels((prev) =>
      prev.includes(value) ? prev.filter((x) => x !== value) : [...prev, value],
    );
  }, []);

  /** Dollars string → whole cents; NaN/negative → 0 (button disables on ≤ 0). */
  const budgetCents = useMemo(() => {
    const parsed = Number.parseFloat(budgetDollars);
    return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed * 100) : 0;
  }, [budgetDollars]);

  const handleOptimize = useCallback(async () => {
    setPlanLoading(true);
    setPlanError(null);
    try {
      const spec: ContactPlanSpec = {
        ...buildSpec(),
        budget_cents: budgetCents,
        channels: planChannels,
        max_tier: maxTier,
      };
      const plan = await getContactPlan(spec);
      setPlanResult(plan);
    } catch (e: unknown) {
      setPlanError(parseError(e));
      setPlanResult(null);
    } finally {
      setPlanLoading(false);
    }
  }, [buildSpec, budgetCents, planChannels, maxTier, parseError]);

  // ---- Access wall --------------------------------------------------------
  if (!canView) {
    return (
      <div className="min-h-dvh bg-bg-base text-fg-primary p-6">
        <div className="max-w-2xl mx-auto rounded-lg border border-status-danger-bg/30 bg-status-danger-bg/20 p-6">
          <h2 className="text-lg font-semibold text-status-danger mb-2">Access restricted</h2>
          <p className="text-fg-secondary">
            The Channel Planner is available to analysts, campaign managers, managers, and
            administrators.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ViewHeader
        title={
          <span className="flex items-center gap-2">
            <Scale className="w-6 h-6 text-accent" />
            Channel Planner
          </span>
        }
        tagline="Compare outreach channels by cost per expected vote, then rank them on a live weighted decision matrix."
      />

      {/* Audience selector */}
      <div className="bg-bg-raised rounded-xl border border-border-base p-4 sm:p-5 space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Election date">
            <input
              type="date"
              value={electionDate}
              onChange={(e) => setElectionDate(e.target.value)}
              className="w-full bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm"
            />
          </Field>
          <Field label="Tier bundle">
            <select
              value={tier}
              onChange={(e) => setTier(e.target.value as MailListTier)}
              className="w-full bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm"
              title="Which cumulative tier bundle defines the audience"
            >
              {TIER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {/* Contest scope: candidates (optional — empty = whole tenant) */}
        <div>
          <div className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold mb-1.5">
            Contest scope — candidates (optional)
          </div>
          {candidatesError ? (
            <p className="text-xs text-status-warning">{candidatesError}</p>
          ) : candidates.length === 0 ? (
            <p className="text-xs text-fg-muted">
              No active candidates found — the whole tenant will be analyzed.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {candidates.map((c) => {
                const active = selectedCandidateIds.includes(c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => toggleCandidate(c.id)}
                    aria-pressed={active}
                    className={`text-xs rounded-full border px-3 py-1.5 transition-colors ${
                      active
                        ? 'bg-accent text-white border-accent'
                        : 'bg-bg-base text-fg-secondary border-border-base hover:border-border-strong'
                    }`}
                    title={`${c.office}${c.district_label ? ` · ${c.district_label}` : ''}`}
                  >
                    {candidateShortName(c)}
                    {c.district_label ? (
                      <span className={active ? 'opacity-80' : 'text-fg-muted'}>
                        {' '}
                        · {c.district_label}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          )}
          <p className="text-[11px] text-fg-muted mt-1.5">
            Pick one or more contests to narrow the audience, or leave empty to analyze the whole
            tenant.
          </p>
        </div>

        {/* Analyze */}
        <div className="flex items-center gap-3 pt-1">
          <button
            type="button"
            onClick={handleAnalyze}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded bg-accent hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
            {loading ? 'Analyzing…' : 'Analyze'}
          </button>
          {!analyzed && (
            <span className="text-xs text-fg-muted">
              Runs cost-per-vote and the decision matrix for this audience.
            </span>
          )}
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-status-danger-bg/30 rounded-xl p-4 border border-status-danger-bg text-status-danger text-sm flex items-center justify-between gap-3">
          <span>{error}</span>
          <button
            onClick={handleAnalyze}
            className="bg-accent hover:bg-accent-hover text-white px-3 py-1.5 rounded text-xs font-bold"
          >
            Retry
          </button>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && !costResult && !matrixResult && (
        <div className="bg-bg-raised rounded-xl border border-dashed border-border-base p-8 text-center">
          <Scale className="w-8 h-8 text-fg-muted mx-auto mb-2" aria-hidden="true" />
          <p className="text-fg-secondary text-sm">
            Define an audience above and hit <span className="font-semibold">Analyze</span> to see
            cost per expected vote and the weighted channel ranking.
          </p>
        </div>
      )}

      {/* Results */}
      {costResult && !loading && <CostPerVoteTable result={costResult} isMobile={isMobile} />}

      {matrixResult && !loading && (
        <>
          <WeightEditor
            weights={weights}
            defaultWeights={defaultWeights}
            dirty={weightsDirty}
            reweighting={reweighting}
            onWeight={setWeight}
            onApply={handleReweight}
            onReset={resetWeights}
          />
          <DecisionMatrixTable result={matrixResult} isMobile={isMobile} />
        </>
      )}

      {/* Budget plan — optimizes a funded contact plan against a spend cap.
          Reuses the audience selector above (candidates + tier + election). */}
      <div className="space-y-3 pt-2">
        <div className="flex items-center gap-2">
          <Wallet className="w-5 h-5 text-accent" aria-hidden="true" />
          <h2 className="text-lg font-bold text-fg-primary">Budget plan</h2>
        </div>
        <p className="text-[11px] text-fg-muted -mt-1">
          Set a budget and the optimizer funds the highest-value contest × tier × channel buys for
          the audience defined above, up to your cap.
        </p>

        <div className="bg-bg-raised rounded-xl border border-border-base p-4 sm:p-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Budget (USD)">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-muted text-sm pointer-events-none">
                  $
                </span>
                <input
                  type="number"
                  min={0}
                  step="100"
                  inputMode="decimal"
                  value={budgetDollars}
                  onChange={(e) => setBudgetDollars(e.target.value)}
                  className="w-full bg-bg-base border border-border-base rounded pl-6 pr-3 py-2 text-fg-primary text-sm tabular-nums"
                  placeholder="5000"
                />
              </div>
            </Field>
            <Field label="Deepest tier to fund">
              <select
                value={maxTier}
                onChange={(e) => setMaxTier(e.target.value as MailListTier)}
                className="w-full bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm"
                title="The optimizer may buy tiers up to and including this bundle"
              >
                {TIER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {/* Paid-channel picker */}
          <div>
            <div className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold mb-1.5">
              Channels the optimizer may fund
            </div>
            <div className="flex flex-wrap gap-3">
              {PLAN_CHANNEL_OPTIONS.map((o) => {
                const checked = planChannels.includes(o.value);
                return (
                  <label
                    key={o.value}
                    className={`inline-flex items-center gap-2 text-xs rounded-lg border px-3 py-2 cursor-pointer transition-colors ${
                      checked
                        ? 'bg-accent/10 border-accent text-fg-primary'
                        : 'bg-bg-base border-border-base text-fg-secondary hover:border-border-strong'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => togglePlanChannel(o.value)}
                      className="accent-accent"
                    />
                    {o.label}
                  </label>
                );
              })}
            </div>
            <p className="text-[11px] text-fg-muted mt-1.5">
              Mail, SMS and digital boost are on by default; canvass is opt-in.
            </p>
          </div>

          {/* Optimize */}
          <div className="flex flex-wrap items-center gap-3 pt-1">
            <button
              type="button"
              onClick={handleOptimize}
              disabled={planLoading || budgetCents <= 0 || planChannels.length === 0}
              className="inline-flex items-center gap-2 rounded bg-accent hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-2"
            >
              {planLoading && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
              {planLoading ? 'Optimizing…' : 'Optimize plan'}
            </button>
            {budgetCents <= 0 && (
              <span className="text-xs text-status-warning">Enter a budget above $0.</span>
            )}
            {budgetCents > 0 && planChannels.length === 0 && (
              <span className="text-xs text-status-warning">Pick at least one channel.</span>
            )}
          </div>
        </div>

        {planError && (
          <div className="bg-status-danger-bg/30 rounded-xl p-4 border border-status-danger-bg text-status-danger text-sm flex items-center justify-between gap-3">
            <span>{planError}</span>
            <button
              onClick={handleOptimize}
              className="bg-accent hover:bg-accent-hover text-white px-3 py-1.5 rounded text-xs font-bold"
            >
              Retry
            </button>
          </div>
        )}

        {planResult && !planLoading && <BudgetPlanResult result={planResult} isMobile={isMobile} />}
      </div>

      {/* Notes (zero-household warnings, etc.) */}
      <Notes
        notes={[
          ...(costResult?.notes ?? []),
          ...(matrixResult?.notes ?? []),
          ...(planResult?.notes ?? []),
        ]}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cost-per-vote table
// ---------------------------------------------------------------------------

function BudgetPlanResult({
  result,
  isMobile,
}: {
  result: ContactPlanResponse;
  isMobile: boolean;
}) {
  const pct =
    result.budget_cents > 0
      ? Math.min(100, Math.round((result.spent_cents / result.budget_cents) * 100))
      : 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-6">
        <div>
          <div className="text-2xl font-bold text-fg-primary tabular-nums">
            {formatCents(result.spent_cents)}{' '}
            <span className="text-sm font-normal text-fg-muted">
              of {formatCents(result.budget_cents)}
            </span>
          </div>
          <div className="text-xs text-fg-muted">
            spent · leftover {formatCents(result.leftover_cents)}
          </div>
        </div>
        <div>
          <div className="text-2xl font-bold text-fg-primary tabular-nums">
            {formatCount(result.total_expected_reach)}
          </div>
          <div className="text-xs text-fg-muted">expected votes reached</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-fg-primary tabular-nums">
            {result.funded_items}
          </div>
          <div className="text-xs text-fg-muted">funded items</div>
        </div>
      </div>

      <div className="h-2 w-full rounded-full bg-bg-base overflow-hidden">
        <div className="h-full bg-accent" style={{ width: `${pct}%` }} />
      </div>

      {result.advisory && (
        <div className="bg-bg-base border border-border-base text-fg-secondary rounded-xl p-3 text-xs">
          {result.advisory}
        </div>
      )}

      {result.plan.length === 0 ? (
        <div className="text-sm text-fg-muted">No bands funded within this budget.</div>
      ) : isMobile ? (
        <div className="space-y-2">
          {result.plan.map((p, i) => (
            <div key={i} className="bg-bg-raised rounded-xl border border-border-base p-4">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-fg-primary font-semibold">{p.contest}</span>
                <span className="text-xs text-fg-muted">{p.tier}</span>
              </div>
              <div className="text-xs text-fg-muted mt-1">
                {p.channel_label} · {formatCount(p.households)} hh · {formatCents(p.cost_cents)}{' '}
                · reach {formatCount(p.expected_reach)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-bg-raised rounded-xl border border-border-base overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-base text-left text-fg-muted">
                  <th className="py-2.5 px-4 font-semibold">Contest</th>
                  <th className="py-2.5 px-4 font-semibold">Tier</th>
                  <th className="py-2.5 px-4 font-semibold">Channel</th>
                  <th className="py-2.5 px-4 font-semibold text-right">Households</th>
                  <th className="py-2.5 px-4 font-semibold text-right">Cost</th>
                  <th className="py-2.5 px-4 font-semibold text-right">Expected reach</th>
                </tr>
              </thead>
              <tbody>
                {result.plan.map((p, i) => (
                  <tr key={i} className="border-b border-border-base last:border-0">
                    <td className="py-2.5 px-4 text-fg-primary">{p.contest}</td>
                    <td className="py-2.5 px-4 text-fg-secondary">{p.tier}</td>
                    <td className="py-2.5 px-4 text-fg-secondary">{p.channel_label}</td>
                    <td className="py-2.5 px-4 text-right tabular-nums text-fg-secondary">
                      {formatCount(p.households)}
                    </td>
                    <td className="py-2.5 px-4 text-right tabular-nums text-fg-secondary">
                      {formatCents(p.cost_cents)}
                    </td>
                    <td className="py-2.5 px-4 text-right tabular-nums text-fg-secondary">
                      {formatCount(p.expected_reach)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function CostPerVoteTable({
  result,
  isMobile,
}: {
  result: CostPerVoteResponse;
  isMobile: boolean;
}) {
  // Bar scale: the cheapest non-null cost/vote fills the bar least; scale the
  // bar width to the MAX so the cheapest visually pops (shortest bar).
  const maxCpv = useMemo(() => {
    const vals = result.channels
      .map((c) => c.cost_per_vote_cents)
      .filter((v): v is number => v != null && v > 0);
    return vals.length ? Math.max(...vals) : 0;
  }, [result.channels]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg font-bold text-fg-primary">Cost per expected vote</h2>
        <div className="text-xs text-fg-muted">
          {formatCount(result.households)} households · {formatCount(result.voters)} voters ·{' '}
          {result.election_date}
        </div>
      </div>

      {isMobile ? (
        <div className="space-y-3">
          {result.channels.map((c, i) => (
            <div key={c.channel} className="bg-bg-raised rounded-xl border border-border-base p-4">
              <div className="flex items-baseline justify-between gap-2 mb-2">
                <span className="text-fg-primary font-semibold">
                  <span className="text-fg-muted tabular-nums mr-1.5">{i + 1}.</span>
                  {c.label}
                </span>
                <span className="text-fg-primary font-bold tabular-nums">
                  {formatCostPerVote(c.cost_per_vote_cents)}
                </span>
              </div>
              <CpvBar value={c.cost_per_vote_cents} max={maxCpv} />
              <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm mt-2">
                <dt className="text-fg-muted">Cost</dt>
                <dd className="text-right tabular-nums text-fg-secondary">
                  {formatCents(c.cost_cents)}
                </dd>
                <dt className="text-fg-muted">Expected reach</dt>
                <dd className="text-right tabular-nums text-fg-secondary">
                  {formatCount(c.expected_reach)}
                </dd>
              </dl>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-bg-raised rounded-xl border border-border-base overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-base text-left text-fg-muted">
                  <th className="py-2.5 px-4 font-semibold w-8">#</th>
                  <th className="py-2.5 px-4 font-semibold">Channel</th>
                  <th className="py-2.5 px-4 font-semibold text-right">Cost</th>
                  <th className="py-2.5 px-4 font-semibold text-right">Expected reach</th>
                  <th className="py-2.5 px-4 font-semibold text-right">Cost / vote</th>
                  <th className="py-2.5 px-4 font-semibold w-40">&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                {result.channels.map((c, i) => (
                  <tr
                    key={c.channel}
                    className="border-b border-border-base last:border-0 hover:bg-bg-base/40"
                  >
                    <td className="py-2.5 px-4 text-fg-muted tabular-nums">{i + 1}</td>
                    <td className="py-2.5 px-4 text-fg-primary font-medium">{c.label}</td>
                    <td className="py-2.5 px-4 text-right tabular-nums text-fg-secondary">
                      {formatCents(c.cost_cents)}
                    </td>
                    <td className="py-2.5 px-4 text-right tabular-nums text-fg-secondary">
                      {formatCount(c.expected_reach)}
                    </td>
                    <td className="py-2.5 px-4 text-right tabular-nums text-fg-primary font-semibold">
                      {formatCostPerVote(c.cost_per_vote_cents)}
                    </td>
                    <td className="py-2.5 px-4">
                      <CpvBar value={c.cost_per_vote_cents} max={maxCpv} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="text-[11px] text-fg-muted">
        Ranked cheapest-first. Cost / vote = channel cost ÷ expected reach; &ldquo;—&rdquo; means the
        expected reach was zero.
      </p>
    </div>
  );
}

/**
 * A horizontal bar proportional to cost/vote — cheaper channels get shorter
 * bars so the winner visually pops. `null`/zero-max render as empty.
 */
function CpvBar({ value, max }: { value: number | null; max: number }) {
  const pct = value != null && max > 0 ? Math.max(4, Math.round((value / max) * 100)) : 0;
  return (
    <div className="h-2 w-full rounded-full bg-bg-base overflow-hidden" aria-hidden="true">
      <div
        className="h-full rounded-full bg-accent/70"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Weight editor
// ---------------------------------------------------------------------------

function WeightEditor({
  weights,
  defaultWeights,
  dirty,
  reweighting,
  onWeight,
  onApply,
  onReset,
}: {
  weights: Record<string, number>;
  defaultWeights: Record<string, number> | null;
  dirty: boolean;
  reweighting: boolean;
  onWeight: (key: string, raw: string) => void;
  onApply: () => void;
  onReset: () => void;
}) {
  if (!defaultWeights) return null;
  return (
    <div className="bg-bg-raised rounded-xl border border-border-base p-4 space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-bold text-fg-primary">Criteria weights</h3>
        <span className="text-[11px] text-fg-muted">
          Higher weight = the criterion counts for more in the weighted total.
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {CRITERIA_ORDER.map((key) => (
          <Field key={key} label={criterionLabel(key)}>
            <input
              type="number"
              step="0.05"
              min={0}
              value={weights[key] ?? 0}
              onChange={(e) => onWeight(key, e.target.value)}
              className="w-full bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm tabular-nums"
            />
          </Field>
        ))}
      </div>
      <div className="flex items-center gap-3 pt-1">
        <button
          type="button"
          onClick={onApply}
          disabled={reweighting || !dirty}
          className="inline-flex items-center gap-2 rounded bg-accent hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-semibold px-3 py-2"
        >
          {reweighting && <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />}
          {reweighting ? 'Re-ranking…' : 'Apply weights'}
        </button>
        <button
          type="button"
          onClick={onReset}
          disabled={!dirty || reweighting}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-fg-secondary hover:text-fg-primary disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" />
          Reset to defaults
        </button>
        {dirty && !reweighting && (
          <span className="text-[11px] text-status-warning">
            Weights changed — apply to re-rank.
          </span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Decision matrix
// ---------------------------------------------------------------------------

function DecisionMatrixTable({
  result,
  isMobile,
}: {
  result: DecisionMatrixResponse;
  isMobile: boolean;
}) {
  // Show criteria in our canonical order, but only those the server returned.
  const criteria = useMemo(() => {
    const returned = new Set(result.criteria);
    const ordered = CRITERIA_ORDER.filter((k) => returned.has(k));
    const extras = result.criteria.filter((k) => !CRITERIA_ORDER.includes(k as never));
    return [...ordered, ...extras];
  }, [result.criteria]);

  const weightsLegend = criteria
    .map((k) => `${criterionLabel(k)} ${formatScore(result.weights[k] ?? 0)}`)
    .join(' · ');

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg font-bold text-fg-primary">Weighted decision matrix</h2>
        <div className="text-xs text-fg-muted">
          {formatCount(result.households)} households · {formatCount(result.voters)} voters
        </div>
      </div>

      <div className="text-[11px] text-fg-muted">
        Weights: {weightsLegend}. Scores are 1–5.{' '}
        <span className="text-accent font-semibold">{criterionLabel(DERIVED_CRITERION)}</span> is
        derived live from the cost engine.
      </div>

      {isMobile ? (
        <div className="space-y-3">
          {result.channels.map((row, i) => (
            <div
              key={row.channel}
              className={`rounded-xl border p-4 ${
                i === 0
                  ? 'bg-accent/10 border-accent'
                  : 'bg-bg-raised border-border-base'
              }`}
            >
              <div className="flex items-baseline justify-between gap-2 mb-2">
                <span className="text-fg-primary font-semibold">
                  <span className="text-fg-muted tabular-nums mr-1.5">{i + 1}.</span>
                  {row.label}
                  {i === 0 && (
                    <span className="ml-2 text-[10px] uppercase tracking-wide text-accent font-bold">
                      Winner
                    </span>
                  )}
                </span>
                <span className="text-fg-primary font-bold tabular-nums">
                  {formatScore(row.weighted_total)}
                </span>
              </div>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                {criteria.map((k) => (
                  <React.Fragment key={k}>
                    <dt
                      className={
                        k === DERIVED_CRITERION ? 'text-accent' : 'text-fg-muted'
                      }
                    >
                      {criterionLabel(k)}
                    </dt>
                    <dd className="text-right tabular-nums text-fg-secondary">
                      {formatScore(row.scores[k] ?? 0)}
                    </dd>
                  </React.Fragment>
                ))}
                <dt className="text-fg-muted pt-1 border-t border-border-base mt-1">Cost / vote</dt>
                <dd className="text-right tabular-nums text-fg-secondary pt-1 border-t border-border-base mt-1">
                  {formatCostPerVote(row.cost_per_vote_cents)}
                </dd>
              </dl>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-bg-raised rounded-xl border border-border-base overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-base text-left text-fg-muted">
                  <th className="py-2.5 px-4 font-semibold w-8">#</th>
                  <th className="py-2.5 px-4 font-semibold">Channel</th>
                  {criteria.map((k) => (
                    <th
                      key={k}
                      className={`py-2.5 px-3 font-semibold text-right whitespace-nowrap ${
                        k === DERIVED_CRITERION ? 'text-accent' : ''
                      }`}
                      title={
                        k === DERIVED_CRITERION
                          ? 'Derived live from the cost engine'
                          : `Weight ${formatScore(result.weights[k] ?? 0)}`
                      }
                    >
                      {criterionLabel(k)}
                      {k === DERIVED_CRITERION && (
                        <span className="block text-[9px] font-bold uppercase tracking-wide text-accent/80">
                          live
                        </span>
                      )}
                    </th>
                  ))}
                  <th className="py-2.5 px-4 font-semibold text-right">Weighted</th>
                </tr>
              </thead>
              <tbody>
                {result.channels.map((row, i) => (
                  <tr
                    key={row.channel}
                    className={`border-b border-border-base last:border-0 ${
                      i === 0 ? 'bg-accent/10' : 'hover:bg-bg-base/40'
                    }`}
                  >
                    <td className="py-2.5 px-4 text-fg-muted tabular-nums">{i + 1}</td>
                    <td className="py-2.5 px-4 text-fg-primary font-medium whitespace-nowrap">
                      {row.label}
                      {i === 0 && (
                        <span className="ml-2 text-[10px] uppercase tracking-wide text-accent font-bold">
                          Winner
                        </span>
                      )}
                    </td>
                    {criteria.map((k) => (
                      <td
                        key={k}
                        className={`py-2.5 px-3 text-right tabular-nums ${
                          k === DERIVED_CRITERION
                            ? 'text-accent bg-accent/5'
                            : 'text-fg-secondary'
                        }`}
                      >
                        {formatScore(row.scores[k] ?? 0)}
                      </td>
                    ))}
                    <td className="py-2.5 px-4 text-right tabular-nums text-fg-primary font-bold">
                      {formatScore(row.weighted_total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="text-[11px] text-fg-muted">
        Ranked best-first by weighted total. The winning row is highlighted. Cost efficiency is
        computed live from the cost-per-vote engine; the other criteria are channel baselines.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Notes
// ---------------------------------------------------------------------------

function Notes({ notes }: { notes: string[] }) {
  // De-dup — cost-per-vote and the matrix often emit the same warning.
  const unique = useMemo(() => Array.from(new Set(notes.filter((n) => n.trim().length > 0))), [notes]);
  if (unique.length === 0) return null;
  return (
    <div className="bg-status-warning-bg/20 rounded-xl p-3 border border-status-warning-bg/40 text-status-warning text-sm space-y-1">
      {unique.map((n, i) => (
        <p key={i}>{n}</p>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small field primitive
// ---------------------------------------------------------------------------

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold">{label}</span>
      {children}
    </label>
  );
}

export default ChannelPlannerView;
